import React from 'react';
import { Building2, TrendingUp, Zap, CheckCircle } from 'lucide-react';

function ManufacturersDTC() {
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="text-5xl font-bold mb-6 text-[#000000]">
          Manufacturers (Asia, LATAM) Launching DTC Brands in the Americas
        </h1>
        
        <p className="text-xl text-gray-700 mb-12">
          You have the manufacturing expertise and product quality. Now it's time to capture more value by building your own direct-to-consumer brand. But DTC is a different game with its own challenges and opportunities.
        </p>

        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6 text-[#000000]">The DTC Transition Challenge</h2>
          <div className="space-y-4">
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <h3 className="text-xl font-semibold mb-2 text-[#000000]">Brand Building from Scratch</h3>
              <p className="text-gray-700">Transitioning from B2B manufacturing to consumer-facing brand requires new skills in branding, positioning, and consumer psychology.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <h3 className="text-xl font-semibold mb-2 text-[#000000]">Digital Marketing Expertise</h3>
              <p className="text-gray-700">Understanding e-commerce platforms, digital advertising, SEO, social media, and customer acquisition strategies.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <h3 className="text-xl font-semibold mb-2 text-[#000000]">Direct Customer Relationships</h3>
              <p className="text-gray-700">Managing customer service, returns, reviews, and building community—all new capabilities for manufacturers.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <h3 className="text-xl font-semibold mb-2 text-[#000000]">Channel Management</h3>
              <p className="text-gray-700">Balancing DTC operations with existing B2B relationships without channel conflict.</p>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6 text-[#000000]">How We Help Manufacturers Go DTC</h2>
          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#38A3A5] rounded-lg flex items-center justify-center flex-shrink-0">
                <Building2 size={24} className="text-white" />
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2 text-[#000000]">DTC Brand Building</h3>
                <p className="text-gray-700 mb-3">We help you create a compelling consumer brand that leverages your manufacturing strengths while resonating with end customers.</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-[#BB3E00] mt-1 flex-shrink-0" />
                    <span className="text-sm">Brand strategy, positioning, and identity development</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-[#BB3E00] mt-1 flex-shrink-0" />
                    <span className="text-sm">Consumer insights and target market definition</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-[#BB3E00] mt-1 flex-shrink-0" />
                    <span className="text-sm">Brand storytelling and content strategy</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#38A3A5] rounded-lg flex items-center justify-center flex-shrink-0">
                <Zap size={24} className="text-white" />
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2 text-[#000000]">E-Commerce Launch & Optimization</h3>
                <p className="text-gray-700 mb-3">Set up and optimize your digital sales channels for maximum conversion and customer lifetime value.</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-[#BB3E00] mt-1 flex-shrink-0" />
                    <span className="text-sm">Shopify/WooCommerce store setup and optimization</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-[#BB3E00] mt-1 flex-shrink-0" />
                    <span className="text-sm">Amazon and marketplace strategy</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-[#BB3E00] mt-1 flex-shrink-0" />
                    <span className="text-sm">Payment processing and fulfillment integration</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#38A3A5] rounded-lg flex items-center justify-center flex-shrink-0">
                <TrendingUp size={24} className="text-white" />
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2 text-[#000000]">Marketing & Customer Acquisition</h3>
                <p className="text-gray-700 mb-3">Build sustainable customer acquisition engines with proven digital marketing strategies.</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-[#BB3E00] mt-1 flex-shrink-0" />
                    <span className="text-sm">Digital advertising (Google, Meta, TikTok)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-[#BB3E00] mt-1 flex-shrink-0" />
                    <span className="text-sm">Influencer partnerships and content marketing</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-[#BB3E00] mt-1 flex-shrink-0" />
                    <span className="text-sm">Email marketing and retention strategies</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-[#BB3E00] mt-1 flex-shrink-0" />
                    <span className="text-sm">SEO and organic growth strategies</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#38A3A5] text-white p-8 rounded-2xl">
          <h2 className="text-2xl font-bold mb-4">Ready to Launch Your DTC Brand?</h2>
          <p className="mb-6">Let's discuss your manufacturing strengths and build a winning DTC strategy.</p>
          <a
            href="mailto:contact@motifunbound.com?subject=Schedule%20a%20Consult&body=Hi%20Motif%20Unbound%20team,%0D%0A%0D%0AI%27d%20like%20to%20schedule%20a%20consultation%20to%20discuss%20our%20market%20expansion.%20Please%20share%20available%20times.%0D%0A%0D%0AThank%20you."
            className="inline-block bg-[#FFD965] hover:bg-[#f5cc4d] text-[#000000] px-8 py-3 rounded-lg font-semibold transition-all"
          >
            Schedule a Consult
          </a>
        </section>
      </div>
    </div>
  );
}

export default ManufacturersDTC;
